/**
 * A radix-2 FFT, in place, real input.
 *
 * Here because offline analysis cannot use the browser's `AnalyserNode`. That
 * node is a *realtime* instrument: it samples whatever the audio graph happens
 * to be playing at the moment it is asked, which is exactly the wall-clock
 * dependence offline rendering exists to escape. Analysing the decoded samples
 * directly gives the same spectrum every time, on any machine, at any speed.
 *
 * Deliberately small and unclever. This runs a few thousand times per song at
 * analysis time and never once per frame, so an optimised implementation would
 * buy nothing and cost the ability to read it.
 */

/** Bit-reversal permutation table for a given size. Cached per size. */
const reversalCache = new Map<number, Uint32Array>();

function reversalTable(size: number): Uint32Array {
  const cached = reversalCache.get(size);
  if (cached !== undefined) return cached;

  const bits = Math.log2(size);
  const table = new Uint32Array(size);
  for (let index = 0; index < size; index += 1) {
    let reversed = 0;
    for (let bit = 0; bit < bits; bit += 1) {
      reversed = (reversed << 1) | ((index >>> bit) & 1);
    }
    table[index] = reversed;
  }
  reversalCache.set(size, table);
  return table;
}

export function isPowerOfTwo(value: number): boolean {
  return value > 0 && (value & (value - 1)) === 0;
}

/**
 * Transforms `real`/`imag` in place.
 *
 * Both arrays must be the same power-of-two length. For real input, fill `imag`
 * with zeroes; the result's first half is the meaningful spectrum.
 */
export function fft(real: Float32Array, imag: Float32Array): void {
  const size = real.length;
  if (!isPowerOfTwo(size)) throw new RangeError(`FFT size ${size} is not a power of two`);
  if (imag.length !== size) throw new RangeError('real and imag must be the same length');

  const reversed = reversalTable(size);
  for (let index = 0; index < size; index += 1) {
    const target = reversed[index] as number;
    if (target > index) {
      const tempReal = real[index] as number;
      real[index] = real[target] as number;
      real[target] = tempReal;
      const tempImag = imag[index] as number;
      imag[index] = imag[target] as number;
      imag[target] = tempImag;
    }
  }

  for (let span = 2; span <= size; span *= 2) {
    const half = span / 2;
    const step = (-2 * Math.PI) / span;
    for (let start = 0; start < size; start += span) {
      for (let offset = 0; offset < half; offset += 1) {
        const angle = step * offset;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const evenIndex = start + offset;
        const oddIndex = evenIndex + half;

        const oddReal = (real[oddIndex] as number) * cos - (imag[oddIndex] as number) * sin;
        const oddImag = (real[oddIndex] as number) * sin + (imag[oddIndex] as number) * cos;

        real[oddIndex] = (real[evenIndex] as number) - oddReal;
        imag[oddIndex] = (imag[evenIndex] as number) - oddImag;
        real[evenIndex] = (real[evenIndex] as number) + oddReal;
        imag[evenIndex] = (imag[evenIndex] as number) + oddImag;
      }
    }
  }
}

/**
 * A Hann window, cached per size.
 *
 * Windowing matters more than it looks: an unwindowed frame of music leaks
 * energy from every partial across the whole spectrum, which turns the
 * three-band split into three copies of the same number.
 */
const windowCache = new Map<number, Float32Array>();

export function hannWindow(size: number): Float32Array {
  const cached = windowCache.get(size);
  if (cached !== undefined) return cached;
  const window = new Float32Array(size);
  for (let index = 0; index < size; index += 1) {
    window[index] = 0.5 * (1 - Math.cos((2 * Math.PI * index) / (size - 1)));
  }
  windowCache.set(size, window);
  return window;
}

/** Magnitudes of the lower half of the spectrum, written into `into`. */
export function magnitudeSpectrum(
  real: Float32Array,
  imag: Float32Array,
  into: Float32Array,
): void {
  const bins = into.length;
  for (let bin = 0; bin < bins; bin += 1) {
    const r = real[bin] as number;
    const i = imag[bin] as number;
    into[bin] = Math.sqrt(r * r + i * i);
  }
}
